/* eslint-disable indent */
import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './styles.css';
import { twMerge } from 'tailwind-merge';
import { ErrorMessage } from '@/components/Message/ErrorMessage';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  errorMessage?: string;
  showToolbar?: boolean;
  isReadOnly?: boolean;
  containerClassName?: string;
  editorClassName?: string;
}

export const RichTextEditor = ({
  value,
  onChange,
  errorMessage,
  showToolbar = true,
  isReadOnly = false,
  containerClassName,
  editorClassName,
}: RichTextEditorProps) => {
  // Ref for the DOM container
  const editorContainerRef = useRef(null);
  // Ref for the Quill instance
  const quillInstanceRef = useRef(null);

  useEffect(() => {
    if (!quillInstanceRef.current) {
      // Initialize Quill only once
      quillInstanceRef.current = new Quill(editorContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: showToolbar
            ? [
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                // ['link', 'image'],
                ['link'],
              ]
            : false,
        },
        readOnly: isReadOnly,
      });

      // Set the initial value
      quillInstanceRef.current.root.innerHTML = value || '';

      // Listen for text changes
      quillInstanceRef.current.on('text-change', () => {
        if (onChange) {
          onChange(quillInstanceRef.current.root.innerHTML);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the content when `value` changes without reinitializing Quill
  useEffect(() => {
    if (
      quillInstanceRef.current &&
      quillInstanceRef.current.root.innerHTML !== value
    ) {
      quillInstanceRef.current.root.innerHTML = value || '';
    }
  }, [value]);

  return (
    <div
      className={twMerge('flex flex-1 flex-col gap-0.75', containerClassName)}
    >
      <div className={twMerge('', errorMessage && 'has-error')}>
        <div
          ref={editorContainerRef}
          className={twMerge('h-72', editorClassName)}
        />
      </div>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
};
